import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import TimeRangeInput from "@/components/TimeRangeInput";
import AssigneesPicker from "@/components/AssigneesPicker";
import { ArrowsClockwise, LightbulbFilament, UsersThree } from "@phosphor-icons/react";

/**
 * Convert a SAV ticket → BKflow intervention (type=depannage).
 * Requires a chantier target + technician + scheduled slot. Assignee_ids optional.
 */
export default function ConvertTicketDialog({ open, onOpenChange, ticket, onConverted }) {
  const [chantiers, setChantiers] = useState([]);
  const [team, setTeam] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { setForm({}); return; }
    const now = new Date();
    const later = new Date(now.getTime() + 60 * 60 * 1000);
    const iso = `${later.toISOString().slice(0, 10)}T${String(later.getHours()).padStart(2, "0")}:00`;
    setForm({
      chantier_id: ticket?.chantier_id || "",
      technician_id: "",
      assignee_ids: [],
      scheduled_date: iso,
      duration_min: 60,
    });
    (async () => {
      try {
        const [c, u] = await Promise.all([api.get("/chantiers"), api.get("/users")]);
        setChantiers(c.data || []);
        setTeam((u.data || []).filter((x) => x.is_active !== false));
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, [open, ticket]);

  const submit = async () => {
    if (!form.chantier_id) { toast.error("Choisis un chantier"); return; }
    if (!form.technician_id) { toast.error("Choisis un tech principal"); return; }
    if (!form.scheduled_date) { toast.error("Choisis un créneau"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/sav-tickets/${ticket.id}/convert-to-intervention`, form);
      toast.success("Ticket converti en intervention SAV");
      onConverted?.(r.data);
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (!ticket) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#111] border-white/10 text-white rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="convert-ticket-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg flex items-center gap-2">
            <ArrowsClockwise size={18} className="text-[#005CFF]" weight="duotone" /> Convertir en intervention
          </DialogTitle>
          <DialogDescription className="text-[10px] uppercase tracking-widest text-white/50 mt-0.5">
            {ticket.title}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl bg-[#005CFF]/8 border border-[#005CFF]/25 p-3 flex items-start gap-2 mt-2">
          <LightbulbFilament size={14} weight="duotone" className="text-[#7CB2FF] shrink-0 mt-0.5" />
          <div className="text-[11px] text-white/70 leading-relaxed">
            Une intervention <b>SAV/Dépannage</b> sera créée, le tech sera notifié, et le ticket passera automatiquement en « En cours ».
          </div>
        </div>

        <div className="space-y-3 mt-3">
          <div>
            <Label className="text-white/60 text-[10px] uppercase tracking-widest">Chantier *</Label>
            <Select value={form.chantier_id || ""} onValueChange={(v) => setForm((f) => ({ ...f, chantier_id: v }))}>
              <SelectTrigger className="bg-white/5 border-white/10 mt-1" data-testid="convert-chantier"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent className="bg-[#111] border-white/10 text-white max-h-64">
                {chantiers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-white/60 text-[10px] uppercase tracking-widest">Tech principal *</Label>
            <Select value={form.technician_id || ""} onValueChange={(v) => setForm((f) => ({ ...f, technician_id: v }))}>
              <SelectTrigger className="bg-white/5 border-white/10 mt-1" data-testid="convert-tech"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent className="bg-[#111] border-white/10 text-white max-h-64">
                {team.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-xl bg-white/[0.02] border border-white/10 p-3 space-y-2">
            <div className="flex items-center gap-1.5 text-white/60 text-[10px] uppercase tracking-widest">
              <UsersThree size={11} weight="duotone" /> Co-affectés (optionnel)
            </div>
            <AssigneesPicker
              team={team.filter((u) => u.id !== form.technician_id)}
              value={form.assignee_ids || []}
              onChange={(ids) => setForm((f) => ({ ...f, assignee_ids: ids }))}
              testid="convert-assignees"
            />
          </div>

          <TimeRangeInput
            scheduled_date={form.scheduled_date}
            duration_min={form.duration_min}
            onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
            testid="convert-time"
          />
        </div>

        <div className="flex gap-2 mt-4">
          <Button variant="ghost" className="flex-1 border border-white/10" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={saving} className="flex-1 bg-[#005CFF] hover:bg-[#0047CC]" data-testid="convert-submit">
            {saving ? "…" : "Créer l'intervention"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
